import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, getApiError } from '../../lib/api';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { StatusPill } from '../../components/StatusPill';
import { PageScaffold } from '../../components/PageScaffold';
import { cn } from '../../lib/cn';
import { DeliveryTab } from './settings/DeliveryTab';

interface Company {
  id: string;
  name: string;
  legalName: string | null;
  vatNumber: string | null;
  brn: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
}

const TABS = [
  { key: 'company', label: 'Company info' },
  { key: 'tax', label: 'Tax (VAT)' },
  { key: 'payments', label: 'Payment methods' },
  { key: 'delivery', label: 'Delivery' },
];

export function SettingsPage(): JSX.Element {
  const [tab, setTab] = useState<string>('company');

  return (
    <PageScaffold title="Settings" subtitle="Tenant configuration. Changes here affect every shop.">
      <div className="flex flex-wrap gap-1.5 border-b border-rule">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              'px-4 py-2 text-sm font-medium -mb-px border-b-2 transition',
              tab === t.key
                ? 'border-accent text-accent'
                : 'border-transparent text-ink-3 hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'company' && <CompanyTab />}
      {tab === 'tax' && <TaxTab />}
      {tab === 'payments' && <PaymentsTab />}
      {tab === 'delivery' && <DeliveryTab />}
    </PageScaffold>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function CompanyTab(): JSX.Element {
  const qc = useQueryClient();
  const company = useQuery({ queryKey: ['company'], queryFn: () => apiGet<Company>('/company') });
  const [editing, setEditing] = useState<Partial<Company> | null>(null);

  const save = useMutation({
    mutationFn: (data: Partial<Company>) => apiPatch('/company', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company'] });
      setEditing(null);
    },
  });

  if (company.isLoading || !company.data) return <div className="text-ink-3">Loading…</div>;
  const c = company.data;

  return (
    <Card
      title="Company"
      subtitle="Used on every invoice header"
      action={
        editing == null ? (
          <Button variant="secondary" size="sm" onClick={() => setEditing({ ...c })}>
            Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button size="sm" loading={save.isPending} onClick={() => save.mutate(editing!)}>
              Save
            </Button>
          </div>
        )
      }
    >
      {editing == null ? (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <KV k="Trading name" v={c.name} />
          <KV k="Legal name" v={c.legalName ?? '—'} />
          <KV k="VAT" v={<span className="kc-mono">{c.vatNumber ?? '—'}</span>} />
          <KV k="BRN" v={<span className="kc-mono">{c.brn ?? '—'}</span>} />
          <KV k="Phone" v={<span className="kc-mono">{c.phone ?? '—'}</span>} />
          <KV k="Email" v={c.email ?? '—'} />
          <KV k="Address" v={c.address ?? '—'} className="md:col-span-2" />
        </dl>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Trading name"
            value={editing.name ?? ''}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          />
          <Input
            label="Legal name"
            value={editing.legalName ?? ''}
            onChange={(e) => setEditing({ ...editing, legalName: e.target.value })}
          />
          <Input
            label="VAT"
            value={editing.vatNumber ?? ''}
            onChange={(e) => setEditing({ ...editing, vatNumber: e.target.value })}
          />
          <Input
            label="BRN"
            value={editing.brn ?? ''}
            onChange={(e) => setEditing({ ...editing, brn: e.target.value })}
          />
          <Input
            label="Phone"
            value={editing.phone ?? ''}
            onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
          />
          <Input
            label="Email"
            type="email"
            value={editing.email ?? ''}
            onChange={(e) => setEditing({ ...editing, email: e.target.value })}
          />
          <Input
            label="Address"
            value={editing.address ?? ''}
            onChange={(e) => setEditing({ ...editing, address: e.target.value })}
            className="col-span-2"
          />
          {save.isError && (
            <div className="col-span-2 text-xs text-negative">
              {getApiError(save.error).message}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function TaxTab(): JSX.Element {
  return (
    <Card title="VAT" subtitle="Mauritius standard rate · per-product configurable">
      <div className="space-y-4 text-sm">
        <p className="text-ink-2">
          The default product VAT rate is <b>15%</b>. Each product can override its own rate from
          the Products admin screen.
        </p>
        <p className="text-ink-2">
          Customers flagged as <StatusPill tone="info">VAT excluded</StatusPill> bypass VAT entirely
          on every order they place — orders snapshot the exclusion at create time so changing the
          customer flag later won't rewrite historical invoices.
        </p>
        <p className="text-ink-3 text-xs">
          Per-tenant default VAT rate config will land in Phase 2. For now, edit the rate on each
          product or leave the 15% default.
        </p>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function PaymentsTab(): JSX.Element {
  const methods: Array<{
    key: string;
    label: string;
    hint: string;
    tone: 'positive' | 'info' | 'neutral';
  }> = [
    { key: 'cash', label: 'Cash', hint: 'Always counts toward daily cash close', tone: 'positive' },
    {
      key: 'juice',
      label: 'MCB Juice',
      hint: 'Save the Juice ref in the payment reference field',
      tone: 'positive',
    },
    {
      key: 'bank_transfer',
      label: 'Bank transfer',
      hint: 'Wait for clearing before marking the order paid',
      tone: 'info',
    },
    { key: 'card', label: 'Card', hint: 'Manual receipt # entry', tone: 'info' },
    { key: 'cheque', label: 'Cheque', hint: 'Capture cheque number in reference', tone: 'info' },
    {
      key: 'other',
      label: 'Other',
      hint: 'Free-form fallback (specify in reference)',
      tone: 'neutral',
    },
  ];
  return (
    <Card title="Payment methods" subtitle="All methods are available by default in Phase 1">
      <ul className="divide-y divide-rule-2">
        {methods.map((m) => (
          <li key={m.key} className="py-3 flex items-center justify-between">
            <div>
              <div className="text-sm text-ink font-medium">{m.label}</div>
              <div className="text-xs text-ink-3 mt-0.5">{m.hint}</div>
            </div>
            <StatusPill tone={m.tone}>enabled</StatusPill>
          </li>
        ))}
      </ul>
      <p className="text-xs text-ink-3 mt-3">
        Per-shop enable/disable toggles will land in Phase 2. Today all methods are available
        everywhere.
      </p>
    </Card>
  );
}

function KV({
  k,
  v,
  className,
}: {
  k: string;
  v: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={className}>
      <div className="text-xs text-ink-3 uppercase tracking-wide">{k}</div>
      <div className="text-ink mt-1">{v}</div>
    </div>
  );
}
